/* eslint-disable no-nested-ternary */
import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  Fragment,
} from 'react';
import moment from 'moment';
import format from 'date-fns/format';
import { Query, withApollo, useQuery } from 'react-apollo';
import gql from 'graphql-tag';
import GoogleMapReact from 'google-map-react';
import isEqual from 'lodash.isequal';
import pluralize from 'pluralize';
import ReactSVG from 'react-svg';
import mapResearch from 'map-research.svg';
import ListingCard from '../ListingCard';
import RentalFilterBar from './RentalFilterBar';
import useWindowSize from '../../hooks/useWindowSize';
import ListingMarker from './ListingMarker';
import FiltersDropdown from './Dropdowns/FiltersDropdown';
import { ListingMapContext } from '../../context/ListingMapContext';
import ListingFilterBar from './ListingFilterBar';
import RentalCard from '../RentalCard';
import LoadingCard from '../Loading/LoadingCard';
import {
  propertiesQuery,
  communityQuery,
  userQuery,
} from '../../constants/queries';
import city_coordinates from '../../constants/city_coordinates.json';
import city_zoom_levels from '../../constants/city_zoom_levels.json';
import filter_sorting from '../../constants/filter_sorting.json';
import { queryDateFormat } from '../../constants/formats.json';
import NoResults from '../NoResults';
import RentalMarker from './RentalMarker';
import WhiteMarker from '../WhiteMarker';
import MapPagination from './MapPagination';
import PaginationResults from './PaginationResults';
import MapControl from './MapControl';
import LoadingDots from '../Loading/LoadingDots';
import WindowQuery from '../../utils/WindowQuery';
import MobileActiveMarker from './MobileActiveMarker';
import useOutsideClick from '../../hooks/useOutsideClick';
import convertToSentence from '../../utils/convertToSentence';
import capitalize from '../../utils/capitalize';
import DropdownPortal from '../DropdownPortal';
import DatesDropdown from './Dropdowns/DatesDropdown';
import GuestsDropdown from './Dropdowns/GuestsDropdown';
import PriceDropdown from './Dropdowns/PriceDropdown';
import SortDropdown from './Dropdowns/SortDropdown';
import CategoryDropdown from './Dropdowns/CategoryDropdown';
import TypeDropdown from './Dropdowns/TypeDropdown';
import { clientNoVarnish } from '../client';

if (!document.querySelector('[name=csrf-token]').content) {
  console.warning('Could not find csrf-token');
}

const MAP_LISTINGS_QUERY = gql`
  query MAP_LISTINGS_QUERY(
    $in_polygon: [Float]
    $is_type: String
    $is_type_in: [String]
    $is_premier_eq: Boolean
    $category_slug_in: [String]
    $city_name_cont: String
    $page: String
    $rating_sort: Boolean
    $rating_count_sort: Boolean
    $title_cont: String
    $active_special_listing: Boolean
    $affiliate_link: Boolean
  ) {
    listings(
      page: $page
      per: "18"
      rating_sort: $rating_sort
      rating_count_sort: $rating_count_sort
      active_special_listing: $active_special_listing
      affiliate_link: $affiliate_link
      filter: {
        in_polygon: $in_polygon
        is_type_eq: $is_type
        is_type_in: $is_type_in
        category_slug_in: $category_slug_in
        city_name_cont: $city_name_cont
        title_cont: $title_cont
        is_premier_eq: $is_premier_eq
      }
    ) {
      total_count
      per_page
      current_page
      total_pages
      collection {
        id
        title
        city_name
        city_display_name
        slug
        is_type
        is_premier
        instagram_link
        address
        favorited
        twitter_link
        website_url
        latitude
        rating
        rating_count
        longitude
        special_listing
        special_end_date
        small_featured_image_url
        medium_featured_image_url
        featured_image_url
        category_image_url
        affiliate_link
        category {
          id
          name
          slug
        }
      }
    }
  }
`;

const city_ids = {
  destin: 5,
  '30a': 6,
  'miramar-beach': 16,
  sandestin: 16,
  'fort-walton-beach': 14,
};

const MapPage = ({ client }) => {
  const updateQuery = new WindowQuery();

  const {
    showFilters,
    mapCategories,
    rentalSortBy,
    setRentalSortBy,
    listingSortBy,
    setListingSortBy,
    guests,
    minPrice,
    maxPrice,
    propertyType,
    bedrooms,
    bathrooms,
    rentalSearchAmenities,
    mapStartDate,
    mapEndDate,
    communityId,
    setGuests,
    setMapCategories,
    setMinPrice,
    setMaxPrice,
    setPropertyType,
    setBedrooms,
    setBathrooms,
    setRentalSearchAmenities,
    setStartDate,
    setEndDate,
    setCommunityId,
    titleCont,
    setTitleCont,
    mapCenter,
    setMapCenter,
    isTypeIn,
    setIsTypeIn,
    minScore,
    setMinScore,
    maxScore,
    setMaxScore,
    isPremier,
    setIsPremier,
    hoveredItem,
    setHoveredItem,
    zoom,
    setZoom,
    meta,
    setMeta,
    searchOnMove,
    setSearchOnMove,
    showSearchOnMove,
    setShowSearchOnMove,
    city,
    setCity,
    cityId,
    setCityId,
    showDeals,
    setShowDeals,
    datesVisible,
    guestsVisible,
    priceVisible,
    sortByVisible,
    categoriesVisible,
    typesVisible,
  } = useContext(ListingMapContext);

  const cardsWrapperRef = useRef(null);

  const [affiliateLink, setAffiliateLink] = useState(false);

  const [mapVisible, setMapVisible] = useState(false);
  const [bounds, setBounds] = useState(null);
  const [localBounds, setLocalBounds] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [mapType, setMapType] = useState('rentals');
  const [page, setPage] = useState(1);
  const [commCoords, setCommCoords] = useState(null);
  const [initialMapCoordsUsed, setInitialMapCoordsUsed] = useState(false);
  const [polygon, setPolygon] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [oldMapVariables, setOldMapVariables] = useState(null);
  const [changeSearchOnMove, setChangeSearchOnMove] = useState(true);
  const [initialBounds, setInitialBounds] = useState(null);

  const [mobileCardsList, setMobileCardsList] = useState([]);

  const { width } = useWindowSize();
  const isMobile = width <= 768;

  const { data: userData } = useQuery(userQuery, {
    fetchPolicy: 'no-cache',
    client: clientNoVarnish,
  });

  // Google Analytics Event Tracking
  useEffect(() => {
    if (mapData) {
      const queries = window.location.search.substr(1).split('&');
      const mapQueryType = mapType === 'rentals' ? 'properties' : 'listings';

      const checkInQuery = queries.find(q => q.includes('check_in='));
      const checkOutQuery = queries.find(q => q.includes('check_out='));
      const cityQuery = queries.find(q => q.includes('area='));
      const guestsQuery = queries.find(q => q.includes('guests='));

      const listingIds = mapData[mapQueryType].collection.map(listing => {
        let listingIdVariable = 'R';
        if (listing.is_type === 'shopping') listingIdVariable = 'SH';
        else if (listing.is_type === 'services') listingIdVariable = 'SE';
        else if (listing.is_type === 'activities') listingIdVariable = 'A';

        return `${mapType === 'rentals' ? 'V' : listingIdVariable}${
          listing.id
        }`;
      });

      if (window.dataLayer) {
        window.dataLayer.push({
          checkinDate:
            checkInQuery &&
            moment(checkInQuery.split('=')[1]).format('YYYY-MM-DD'),
          checkoutDate:
            checkOutQuery &&
            moment(checkOutQuery.split('=')[1]).format('YYYY-MM-DD'),
          listingId: listingIds && `${listingIds}`,
          listingCity: cityQuery && cityQuery.split('=')[1],
          listingRegion: 'Florida',
          listingCountry: 'United States',
          guestCount: guestsQuery && parseInt(guestsQuery.split('=')[1]),
          searchType: mapQueryType,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapData]);

  useEffect(() => {
    if (searchOnMove) setBounds(localBounds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localBounds, zoom]);

  useEffect(() => {
    const queries = window.location.search.substr(1).split('&');
    const areaQuery = queries.find(q => q.includes('area='));
    const commQuery = queries.find(q => q.includes('comm_id='));
    if (areaQuery) {
      const cityName = areaQuery.split('=')[1];
      // TODO: Refactor
      window.setTimeout(() => {
        setMapCenter({
          ...city_coordinates[`full-${cityName}`],
        });
        setZoom(city_zoom_levels[`full-${cityName}`]);
      }, 200);
    }

    if (commQuery) {
      // Fetch community
      (async function() {
        const { data } = await client.query({
          query: communityQuery,
          variables: { id: parseInt(commQuery.split('=')[1]) },
        });
        const coords = {
          lat: parseFloat(data.community.latitude),
          lng: parseFloat(data.community.longitude),
        };
        // setMapCenter to be community coordinates
        setMapCenter(coords);
        setCommCoords(coords);
      })();
    }
  }, [client, setMapCenter, setZoom]);

  // Grab query params on page load
  useEffect(() => {
    const queries = window.location.search.substr(1).split('&');

    queries.forEach(q => {
      const [queryName, queryValue] = q.split('=');

      switch (queryName) {
        case 'map_type':
          setMapType(queryValue);
          break;

        case 'is_type_in':
          if (queryValue.includes('%2C')) {
            setIsTypeIn(queryValue.split('%2C')); // %2C is the code for a comma ","
          } else {
            setIsTypeIn(queryValue.split(','));
          }
          break;

        case 'city':
          setCity(decodeURI(queryValue).replace(/-/g, ' '));
          break;

        case 'area':
          setCityId(city_ids[queryValue]);
          setCity(decodeURI(queryValue).replace(/-/g, ' '));
          break;

        case 'guests':
          setGuests(parseInt(queryValue));
          break;

        case 'min_price':
          setMinPrice(parseInt(queryValue));
          break;

        case 'max_price':
          setMaxPrice(parseInt(queryValue));
          break;

        case 'property_type':
          setPropertyType(queryValue);
          break;

        case 'beds':
          setBedrooms(parseInt(queryValue));
          break;

        case 'baths':
          setBathrooms(parseInt(queryValue));
          break;

        case 'amenities':
          setRentalSearchAmenities([...queryValue.split(',')]);
          break;

        case 'check_in':
          setStartDate(moment(queryValue, queryDateFormat));
          break;

        case 'check_out':
          setEndDate(moment(queryValue, queryDateFormat));
          break;

        case 'comm_id':
          setSearchOnMove(false);
          setCommunityId(parseInt(queryValue));
          break;

        case 'title_cont':
          setTitleCont(queryValue);
          break;

        case 'categories':
          // TODO: Handle '&' in query string
          setMapCategories(decodeURI(queryValue).split(','));
          break;

        case 'view':
          setMapVisible(queryValue === 'map');
          break;

        case 'zoom':
          setZoom(parseInt(queryValue));
          break;

        case 'rentals_sort_by':
          setRentalSortBy(
            filter_sorting.find(filter => filter.value === queryValue) || null
          );
          break;

        case 'listings_sort_by':
          setListingSortBy(
            filter_sorting.find(filter => filter.value === queryValue) || null
          );
          break;

        case 'is_premier':
          if (window.location.search.includes('type=rentals')) {
            if (queryValue === 'true') setMinScore(100);
            else setMaxScore(99);
          } else {
            setIsPremier(queryValue);
          }
          break;

        case 'polygon':
          setPolygon(queryValue);
          break;

        case 'show_deals':
          setShowDeals(queryValue);
          break;

        case 'affiliate_link':
          setAffiliateLink(queryValue === 'true');
          break;

        default:
          console.error(`No case for query ${queryName} found!`);
          break;
      }
    });
  }, [
    mapType,
    setRentalSearchAmenities,
    setBathrooms,
    setBedrooms,
    setCity,
    setCityId,
    setCommunityId,
    setEndDate,
    setGuests,
    setIsPremier,
    setIsTypeIn,
    setMapCategories,
    setMaxPrice,
    setMaxScore,
    setMinPrice,
    setMinScore,
    setSearchOnMove,
    setStartDate,
    setTitleCont,
    setPropertyType,
    setZoom,
    setRentalSortBy,
    setListingSortBy,
    setShowDeals,
  ]);

  const variables = {
    page: page.toString(),
  };

  // Set is_type_in variable
  if (isTypeIn) variables.is_type_in = isTypeIn;
  // Set city variable
  if (city) {
    if (mapType === 'rentals') variables.city_id = cityId;
    else variables.city_name_cont = city;
  }
  // Set categories variable
  if (mapCategories)
    variables.category_slug_in = mapCategories.map(c =>
      typeof c === 'object' ? c.name : c
    );
  // Set sort variable
  if (rentalSortBy) {
    variables.sort_by = rentalSortBy.value;
  }
  if (listingSortBy) {
    variables[
      listingSortBy.value === 'rating' ? 'rating_sort' : 'rating_count_sort'
    ] = true;
    variables[
      listingSortBy.value === 'rating' ? 'rating_count_sort' : 'rating_sort'
    ] = false;
  }
  if (guests) variables.guests = guests;
  if (minPrice) variables.min_price = parseInt(minPrice);
  if (maxPrice) variables.max_price = parseInt(maxPrice);
  if (propertyType) variables.prop_type = propertyType;
  if (bedrooms) variables.beds = parseInt(bedrooms);
  if (bathrooms) variables.baths = parseInt(bathrooms);
  if (rentalSearchAmenities.length)
    variables.amenity_list = rentalSearchAmenities.join(',');
  // TODO: Add parameters to properties graphql query
  if (mapStartDate) variables.check_in = format(mapStartDate, 'YYYY-MM-DD');
  if (mapEndDate) variables.check_out = format(mapEndDate, 'YYYY-MM-DD');
  if (titleCont) {
    if (mapType === 'rentals') variables.prop_name = titleCont;
    else variables.title_cont = titleCont;
  }
  if (minScore) variables.min_score = minScore;
  if (maxScore) variables.max_score = maxScore;
  if (isPremier) variables.is_premier_eq = isPremier === 'true'; // is_premier_eq requires a boolean
  if (communityId) variables.comm_id = communityId;
  if (polygon) variables.polygon = polygon;
  if (showDeals)
    variables.active_special_listing = showDeals && Boolean(showDeals); // showDeals requires a boolean
  if (affiliateLink) variables.affiliate_link = affiliateLink;

  // Set is_polygon variable
  if (bounds) {
    if (mapType === 'rentals') {
      const { sw, ne } = bounds;
      const stringPolygon = `[${sw.lng.toFixed(6)},${sw.lat.toFixed(
        6
      )},${ne.lng.toFixed(6)},${ne.lat.toFixed(6)}]`;
      variables.polygon = stringPolygon;
    } else {
      variables.in_polygon = [
        bounds.sw.lng,
        bounds.sw.lat,
        bounds.ne.lng,
        bounds.ne.lat,
      ];
    }
  }

  const updateBounds = ({
    bounds: newBounds,
    center: newCenter,
    zoom: newZoom,
  }) => {
    setLocalBounds(newBounds);
    if (!initialBounds) setInitialBounds(newBounds);
    if (!searchOnMove) setShowSearchOnMove(true);
    if (newZoom !== zoom && initialBounds) setZoom(newZoom);
    // Leave as is - area query coordinates won't work if changed
    if (!initialMapCoordsUsed) {
      setMapCenter(mapCenter);
    } else {
      setMapCenter(newCenter);
      setInitialMapCoordsUsed(true);
    }
  };

  const toggleView = () => {
    setMapVisible(!mapVisible);
  };

  // Scroll to top of list section on changes
  useEffect(() => {
    if (!isEqual(variables, oldMapVariables)) {
      setOldMapVariables(variables);
      // Add `cardsWrapperRef.current.scrollTo` because Edge, IE, and Safari IOS hate the web and don't support element.scrollTo
      if (cardsWrapperRef.current && cardsWrapperRef.current.scrollTo)
        cardsWrapperRef.current.scrollTo(0, 0);
    }
  }, [oldMapVariables, variables]);

  useEffect(() => {
    if (activeMarker) {
      const queryType = mapType === 'rentals' ? 'properties' : 'listings';
      const { collection } = mapData[queryType];
      const activeMarkerData = collection.find(
        record => record.id === activeMarker
      );

      const mobileCardsListArray = [];
      if (activeMarkerData) mobileCardsListArray.push(activeMarkerData);
      mobileCardsListArray.push(
        ...collection.filter(item => item.id !== activeMarker || !item.id)
      );

      setMobileCardsList(mobileCardsListArray);
    } else {
      setMobileCardsList([]);
    }
  }, [activeMarker, mapData, mapType]);

  const markerWrapperRef = useRef(null);
  useOutsideClick(markerWrapperRef, true, () => {
    setActiveMarker(false);
  });

  // SEARCH TITLE
  let searchTitle =
    mapType === 'rentals' ? 'Vacation Rentals' : 'Business Listings';
  if (mapType === 'rentals') {
    if (city && !propertyType && !guests) {
      // “Destin Vacation Rentals”
      searchTitle = `${city} Vacation Rentals`;
    }
    if (propertyType && !city && !guests) {
      // “Condos”
      searchTitle = `${pluralize(propertyType)}`;
    }
    if (guests && !city && !propertyType) {
      // “Vacation Rentals 6 Guests”
      searchTitle = `Vacation Rentals ${guests} ${pluralize('Guest', guests)}`;
    }
    if (city && propertyType && !guests) {
      // “Destin Condos”
      searchTitle = `${city} ${pluralize(propertyType)}`;
    }
    if (city && guests && !propertyType) {
      // “Destin Vacation Rentals 6 Guests”
      searchTitle = `${city} Vacation Rentals ${guests} ${pluralize(
        'Guest',
        guests
      )}`;
    }
    if (propertyType && guests && !city) {
      // “Condos 6 Guests”
      searchTitle = `${pluralize(propertyType)} ${guests} ${pluralize(
        'Guest',
        guests
      )}`;
    }
    if (city && propertyType && guests) {
      // “Destin Condos 6 Guests”
      searchTitle = `${city} ${pluralize(propertyType)} ${guests} ${pluralize(
        'Guest',
        guests
      )}`;
    }
  } else {
    if (city && !isTypeIn.length && !mapCategories.length) {
      // “Destin Business Listings”
      searchTitle = `${city} Business Listings`;
    }
    if (isTypeIn.length && !city && !mapCategories.length) {
      // “Activities”
      searchTitle = `${convertToSentence(isTypeIn)}`;
    }
    if (mapCategories.length && !city && !isTypeIn.length) {
      // “Seafood Business Listings”
      searchTitle = `${convertToSentence(
        mapCategories.map(c => pluralize.singular(c))
      )} Business Listings`;
    }
    if (city && isTypeIn.length && !mapCategories.length) {
      // “Destin Activities”
      searchTitle = `${city} ${convertToSentence(isTypeIn)}`;
    }
    if (city && mapCategories.length && !isTypeIn.length) {
      // “Destin Seafood”
      searchTitle = `${city} ${convertToSentence(mapCategories)}`;
    }
    if (
      isTypeIn.length &&
      isTypeIn.length === 1 &&
      !isTypeIn.includes('restaurants') &&
      !!mapCategories.length
    ) {
      // “Golf Courses”
      searchTitle = `${city ? `${city} ` : ''}${convertToSentence(
        mapCategories
      )}`;
    }
    if (
      isTypeIn.length &&
      isTypeIn.length === 1 &&
      isTypeIn.includes('restaurants') &&
      !!mapCategories.length
    ) {
      // “Seafood Restaurants”
      searchTitle = `${city ? `${city} ` : ''}${convertToSentence(
        mapCategories
      )} Restaurants`;
    }
    if (isTypeIn.length && isTypeIn.length !== 1 && !!mapCategories.length) {
      // “Destin Golf Course and Fishing Activities and Services"
      searchTitle = `${city ? `${city} ` : ''}${convertToSentence(
        mapCategories.map(c => pluralize.singular(c))
      )} ${convertToSentence(isTypeIn)}`;
    }
  }
  // SEARCH TITLE

  useEffect(() => {
    if (searchTitle) {
      document.title = `${capitalize(searchTitle)} | Destin Florida`;
    }
  }, [searchTitle]);

  return (
    <Query
      query={mapType === 'rentals' ? propertiesQuery : MAP_LISTINGS_QUERY}
      variables={variables}
      notifyOnNetworkStatusChange
    >
      {({ data, loading, error }) => {
        const queryType = mapType === 'rentals' ? 'properties' : 'listings';

        if (changeSearchOnMove) {
          setShowSearchOnMove(false);
          setChangeSearchOnMove(false);
        }

        if (
          (!mapData && data && data[queryType]) ||
          (data && !isEqual(data, mapData))
        ) {
          setMapData(data);
        }

        // zoom out on map if no results found on initial search
        if (
          zoom === 13 &&
          data &&
          data[queryType] &&
          data[queryType].collection.length === 0
        ) {
          setZoom(11);
        }

        // eslint-disable-next-line
        const per_page =
          data && data[queryType]
            ? queryType === 'properties'
              ? data[queryType].meta.per_page
              : data[queryType].per_page
            : 0;
        // eslint-disable-next-line
        const total_pages =
          data && data[queryType]
            ? queryType === 'properties'
              ? data[queryType].meta.total_pages
              : data[queryType].total_pages
            : 0;
        // eslint-disable-next-line
        const total_count =
          data && data[queryType]
            ? queryType === 'properties'
              ? data[queryType].meta.total_count
              : data[queryType].total_count
            : 0;
        // eslint-disable-next-line
        const current_page =
          data && data[queryType]
            ? queryType === 'properties'
              ? data[queryType].meta.current_page
              : data[queryType].current_page
            : 0;

        const queryMeta = {
          current_page,
          per_page,
          total_count,
          total_pages,
        };

        // Set Meta
        if (!meta || JSON.stringify(meta) !== JSON.stringify(queryMeta)) {
          setMeta(queryMeta);
        }

        if (error) {
          throw new Error(error);
        }

        const responseData =
          data && Object.keys(data).length > 0 && data[queryType]
            ? data[queryType].collection
            : [];

        const showMapResearch = (!searchOnMove && showSearchOnMove) || loading;

        const isPhone = width <= 425;
        const showMobileActiveMarker = isPhone && activeMarker && mapVisible;

        return (
          <div className="map-container">
            <div className="filter-section" id="filter-section">
              {mapType === 'rentals' ? (
                <RentalFilterBar type={mapType} />
              ) : (
                <ListingFilterBar type={mapType} />
              )}
            </div>
            {isMobile && (
              <DropdownPortal
                visible={
                  datesVisible ||
                  guestsVisible ||
                  priceVisible ||
                  sortByVisible ||
                  categoriesVisible ||
                  typesVisible
                }
              >
                {datesVisible && <DatesDropdown visible={datesVisible} />}
                {guestsVisible && <GuestsDropdown />}
                {priceVisible && <PriceDropdown />}
                {sortByVisible && <SortDropdown mapType={mapType} />}
                {categoriesVisible && <CategoryDropdown mapType={mapType} />}
                {typesVisible && <TypeDropdown />}
              </DropdownPortal>
            )}
            <section
              className="cards-wrapper"
              ref={cardsWrapperRef}
              style={{
                display:
                  isMobile && mapVisible && !showFilters ? 'none' : 'block',
              }}
            >
              <FiltersDropdown />

              <h1 className="search-results-title">{searchTitle}</h1>

              {/* on desktop, always show both sections, only show on mobile if map isn't visible */}
              {(!isMobile || !mapVisible) && !showFilters && (
                <div className="card-section">
                  {!loading && total_count > 0 && isMobile && (
                    <PaginationResults
                      totalCount={total_count}
                      firstIndex={per_page * current_page - per_page + 1}
                      lastIndex={Math.min(per_page * current_page, total_count)}
                      queryType={queryType}
                    />
                  )}
                  {loading
                    ? [...Array(10)].map((_, ind) => <LoadingCard key={ind} />)
                    : responseData.map(responseRecord => {
                        return mapType === 'rentals' ? (
                          <RentalCard
                            key={responseRecord.id}
                            property={{ ...responseRecord }}
                            setHoveredItem={setHoveredItem}
                            checkIn={mapStartDate}
                            checkOut={mapEndDate}
                            guests={guests}
                            remote
                          />
                        ) : (
                          <ListingCard
                            key={responseRecord.id}
                            listing={{ ...responseRecord }}
                            setHoveredItem={setHoveredItem}
                          />
                        );
                      })}
                  {data &&
                    data[queryType] &&
                    (!data[queryType].collection.length ? (
                      <NoResults />
                    ) : (
                      <MapPagination
                        totalCount={total_count}
                        perPage={per_page}
                        totalPages={total_pages}
                        currentPage={current_page}
                        queryType={queryType}
                        onPageChange={({ selected }) => {
                          setPage(Math.min(selected + 1, total_pages));
                        }}
                      />
                    ))}
                </div>
              )}
            </section>
            {/* always show map on desktop, only show on mobile if map is visible */}
            {(!isMobile || (isMobile && mapVisible && !showFilters)) && (
              <div className="map-section">
                {communityId && !isMobile && (
                  <MapControl
                    position="bottom left"
                    onClick={() => {
                      updateQuery.remove('comm_id');
                      setCommunityId(null);
                      setSearchOnMove(false);
                    }}
                    name="Remove Community Filter"
                    aria-label="Remove Community Filter"
                    title="Remove Community Filter"
                  >
                    <span>Remove Community Filter</span>
                  </MapControl>
                )}
                {(cityId || city) && !isMobile && (
                  <MapControl
                    position="bottom left"
                    onClick={() => {
                      updateQuery.remove('area');
                      setCity(null);
                      setCityId(null);
                    }}
                    name="Remove Community Filter"
                    aria-label="Remove Community Filter"
                    title="Remove Community Filter"
                  >
                    <span>Remove City Filter</span>
                  </MapControl>
                )}
                <MapControl
                  position="top center"
                  onClick={() => {
                    if (showMapResearch) {
                      setChangeSearchOnMove(true);
                      setBounds(localBounds);
                    } else {
                      setSearchOnMove(!searchOnMove);
                    }
                  }}
                >
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {loading ? (
                    <LoadingDots />
                  ) : showMapResearch ? (
                    <Fragment>
                      <ReactSVG
                        src={mapResearch}
                        className="map-research-icon"
                      />
                      <span>Redo Search</span>
                    </Fragment>
                  ) : (
                    <Fragment>
                      <input
                        aria-label="Search as I move"
                        type="checkbox"
                        checked={searchOnMove}
                      />
                      <span>Search as I move</span>
                    </Fragment>
                  )}
                </MapControl>
                <GoogleMapReact
                  bootstrapURLKeys={{
                    // @TODO: Add this to an environment variable or something
                    key: 'AIzaSyATvnQePj9OTTCZJ6VkugYM8dRGFPJ5HmQ',
                  }}
                  center={mapCenter}
                  zoom={zoom}
                  onChange={updateBounds}
                >
                  {responseData &&
                    responseData.map(record =>
                      mapType === 'rentals' ? (
                        <RentalMarker
                          key={record.id}
                          lat={record.latitude}
                          lng={record.longitude}
                          property={record}
                          changeActiveMarker={setActiveMarker}
                          activeMarker={activeMarker}
                          highlighted={
                            hoveredItem && hoveredItem.id === record.id
                          }
                        />
                      ) : (
                        <ListingMarker
                          key={record.id}
                          lat={record.latitude}
                          lng={record.longitude}
                          listing={record}
                          type={record.is_type}
                          property
                          changeActiveMarker={setActiveMarker}
                          activeMarker={activeMarker}
                          highlighted={
                            hoveredItem && hoveredItem.id === record.id
                          }
                          white_marker
                        />
                      )
                    )}
                  {commCoords !== null && (
                    <WhiteMarker
                      lat={commCoords.lat}
                      lng={commCoords.lng}
                      type="communities"
                    />
                  )}
                </GoogleMapReact>
              </div>
            )}

            {isPhone && (
              <section
                ref={markerWrapperRef}
                className="mobile-active-marker-container"
              >
                <div className="mobile-active-marker-overflow">
                  {showMobileActiveMarker &&
                    mobileCardsList.map(item => (
                      <MobileActiveMarker
                        key={item.id}
                        item={item}
                        itemType={mapType}
                        userData={userData}
                        changeActiveMarker={setActiveMarker}
                        setHoveredItem={setHoveredItem}
                      />
                    ))}
                </div>
              </section>
            )}
            {isMobile && (
              <button
                name={`View as ${mapVisible ? 'List' : 'Map'}`}
                type="button"
                className="mobile-map-toggle"
                onClick={toggleView}
              >
                View as {mapVisible ? 'List' : 'Map'}
              </button>
            )}
          </div>
        );
      }}
    </Query>
  );
};

MapPage.propTypes = {
  // greeting: PropTypes.string.isRequired,
};

export default withApollo(MapPage);
